import * as React from 'react';
import FocusableSurface from '../interfaces/FocusableSurface';
import FocusManagedComponent from './FocusManagedComponent';
import registerTabbableChildren from '../utils/registerTabbableChildren';
import { observer } from 'mobx-react';
import { SurfaceFocusManager } from '../utils/SurfaceFocusManager';

/**
 * Props for focusable surface
 */
export interface FocusableSurfaceComponentProps {
    setFocusedElement: (id: string) => void;
    tabOrderProvider: () => string[];
    initialTabId?: string;
}

/**
 * The component that maintains the focus manager and has public api for interacting with the focusmanager.
 * Registers all tabbable elements that have the tabid property on them, captures tabs and clicks to manage focus
 * within the component
 */
@observer
export default class FocusableSurfaceComponent
    extends React.Component<FocusableSurfaceComponentProps, {}>
    implements FocusableSurface {
    private focusManager: SurfaceFocusManager;
    private rootElement: HTMLElement;

    constructor(props: FocusableSurfaceComponentProps) {
        super(props);
        this.focusManager = new SurfaceFocusManager(props.setFocusedElement);
    }

    render() {
        this.focusManager.defineOrder(this.props.tabOrderProvider());
        this.focusManager.setInitialFocusId(this.props.initialTabId);

        let surface = (
            <div
                tabIndex={-1}
                ref={el => {
                    this.rootElement = el;
                    this.focusManager.registerRootElementReference(el);
                }}>
                {this.props.children}
            </div>
        );

        surface = registerTabbableChildren(surface, this.focusManager);

        return (
            <FocusManagedComponent key={surface.key} focusManager={this.focusManager}>
                {surface}
            </FocusManagedComponent>
        );
    }

    /**
     * Sets focus to the actual focused element
     */
    public focusSurface() {
        this.focusManager.focusOnFocusedElement();
    }

    /**
     * Sets focus to a particular item
     * @param id tabId of the item to focus
     */
    public focusItem(id: string) {
        setTimeout(this.focusManager.focusOnItem.bind(this.focusManager), 0, id);
    }

    /**
     * Checks whether the surface currently has focus
     */
    public surfaceHasFocus() {
        return (
            this.rootElement && document.activeElement && this.rootElement == document.activeElement
        );
    }
}
